import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useOrg } from "../lib/session";
import { dateTime, roleLabel } from "../lib/format";
import type { Member } from "../lib/types";
import { ErrorText, Field, Form, Loading, PageHeader, str } from "../components/ui";

interface Invite {
  id: string;
  email: string;
  crm_role: string | null;
  expires_at: string;
}

export default function Team() {
  const { base, orgId, memberId } = useOrg();
  const qc = useQueryClient();
  const members = useQuery({ queryKey: [orgId, "members"], queryFn: () => api<{ members: Member[] }>(`${base}/members`) });
  const invites = useQuery({ queryKey: [orgId, "invites"], queryFn: () => api<{ invites: Invite[] }>(`${base}/invites`) });
  const [link, setLink] = useState<string | null>(null);
  const [emailed, setEmailed] = useState(false);
  const refresh = () => {
    qc.invalidateQueries({ queryKey: [orgId, "members"] });
    qc.invalidateQueries({ queryKey: [orgId, "invites"] });
  };
  const update = useMutation({
    mutationFn: ({ id, ...json }: { id: string; crm_role?: string; status?: string }) => api(`${base}/members/${id}`, { method: "PATCH", json }),
    onSuccess: refresh,
  });
  if (members.isLoading) return <Loading />;
  return (
    <>
      <PageHeader title="Team" />
      <ErrorText error={members.error ?? update.error} />
      <table className="table">
        <thead><tr><th>Name</th><th>Email</th><th>CRM role</th><th>Status</th><th></th></tr></thead>
        <tbody>
          {members.data?.members.map((m) => (
            <tr key={m.id}>
              <td>{m.display_name}{m.organization_role === "owner" && <span className="tag">owner</span>}</td>
              <td>{m.email}</td>
              <td>
                <select value={m.crm_role ?? ""} disabled={m.id === memberId || m.status !== "active"} onChange={(e) => update.mutate({ id: m.id, crm_role: e.target.value })} aria-label="CRM role">
                  {!m.crm_role && <option value="">No access</option>}
                  {Object.entries(roleLabel).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </td>
              <td>{m.status}</td>
              <td>
                {m.id !== memberId && m.organization_role !== "owner" && (
                  <button className="link" onClick={() => update.mutate({ id: m.id, status: m.status === "active" ? "disabled" : "active" })}>
                    {m.status === "active" ? "Disable" : "Enable"}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <section className="card">
        <h2>Invite someone</h2>
        <Form submitLabel="Create invite" onSubmit={async (d) => {
          const r = await api<{ invite_url: string; email_sent: boolean }>(`${base}/invites`, { method: "POST", json: { email: str(d, "email"), crm_role: str(d, "crm_role") } });
          setLink(r.invite_url);
          setEmailed(r.email_sent);
          refresh();
        }}>
          <div className="row">
            <Field label="Email"><input name="email" type="email" required /></Field>
            <Field label="CRM role">
              <select name="crm_role" defaultValue="crm_employee">
                {Object.entries(roleLabel).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>
          </div>
        </Form>
        {link && (
          <div className="notice">
            <p>{emailed ? "We emailed the invitation. If it doesn't arrive, share this link" : "The email couldn't be sent. Share this link with the person"}. It works once, expires in 7 days and won't be shown again.</p>
            <pre className="code">{link}</pre>
            <button className="secondary" onClick={() => navigator.clipboard.writeText(link)}>Copy link</button>
          </div>
        )}
        {(invites.data?.invites.length ?? 0) > 0 && (
          <>
            <h3>Pending invites</h3>
            <ul className="list">
              {invites.data!.invites.map((i) => (
                <li key={i.id}>
                  {i.email} <span className="muted small">· {i.crm_role && roleLabel[i.crm_role]} · expires {dateTime(i.expires_at)}</span>{" "}
                  <button className="link" onClick={async () => { await api(`${base}/invites/${i.id}`, { method: "DELETE" }); refresh(); }}>Revoke</button>
                </li>
              ))}
            </ul>
          </>
        )}
      </section>
    </>
  );
}
